/**
 * ManagementDashboard Page
 *
 * Landing page for Platform Management Console
 * Shows platform-wide analytics with enhanced hierarchy view and attention items
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  ChartBarIcon,
  ChartPieIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { Building2, Users, Briefcase } from 'lucide-react';
import { PageTransition, PageTransitionItem } from '@/components/ui/PageTransition';
import { AnimatedCard, AnimatedCardContent } from '@/components/ui/AnimatedCard';
import { API_BASE_URL } from '../config/api';

interface DashboardData {
  hierarchy: {
    enterprises: number;
    channelPartners: number;
    organizations: number;
    billableUsers: number;
    totalUsers: number;
  };
  metrics: {
    totalEncounters: number;
    activeOrganizations: number;
    trialingOrganizations: number;
  };
  attention: {
    pendingDeletions: number;
    highUsageOrgs: number;
    expiringTrials: number;
  };
  recentActivity: Array<{
    type: string;
    organizationName: string;
    partnerName: string | null;
    timestamp: string;
  }>;
  trends: {
    newOrganizationsLast30Days: number;
    newUsersLast30Days: number;
  };
}

const ManagementDashboard: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      const token = localStorage.getItem('accessToken');
      if (!token) return;

      // Roles without this permission are redirected below; do not fire a request
      // they cannot be authorized for.
      if (!hasPermission('platform.analytics.view')) return;

      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/v1/platform/analytics/dashboard`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch dashboard: ${response.status}`);
        }

        const result = await response.json();
        if (result.success && result.data) {
          setData(result.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Set default data on error
        setData({
          hierarchy: { enterprises: 0, channelPartners: 0, organizations: 0, billableUsers: 0, totalUsers: 0 },
          metrics: { totalEncounters: 0, activeOrganizations: 0, trialingOrganizations: 0 },
          attention: { pendingDeletions: 0, highUsageOrgs: 0, expiringTrials: 0 },
          recentActivity: [],
          trends: { newOrganizationsLast30Days: 0, newUsersLast30Days: 0 }
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
    // hasPermission keeps a stable store identity, so depend on the user it reads.
  }, [user]);

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toLocaleString();
  };

  const formatTimeAgo = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const totalAttentionItems = data ?
    data.attention.pendingDeletions + data.attention.highUsageOrgs + data.attention.expiringTrials : 0;

  // This page is platform-wide reporting. Roles scoped to a partner land on the
  // organizations list, which is filtered to them, rather than on an empty console home.
  // Checked before the loading gate: the fetch above never runs for them, so `loading`
  // would otherwise stay true forever.
  if (!hasPermission('platform.analytics.view')) {
    return <Navigate to="/management/organizations" replace />;
  }

  if (loading) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center h-64">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          >
            <div className="h-12 w-12 border-4 border-purple-600 border-t-transparent rounded-full" />
          </motion.div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition className="space-y-6" stagger staggerDelay={0.05}>
      {/* Welcome Header */}
      <PageTransitionItem>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome back, {user?.firstName}
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Platform Management Console - Overview
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <ClockIcon className="h-4 w-4" />
            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
      </PageTransitionItem>

      {/* Quick Stats Row */}
      <PageTransitionItem>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
          <StatCard
            label="Partners"
            value={data?.hierarchy.channelPartners || 0}
            icon={<Briefcase className="h-4 w-4" />}
            color="violet"
          />
          <StatCard
            label="Orgs"
            value={data?.hierarchy.organizations || 0}
            icon={<Building2 className="h-4 w-4" />}
            color="purple"
          />
          <StatCard
            label="Users"
            value={data?.hierarchy.totalUsers || 0}
            icon={<Users className="h-4 w-4" />}
            color="blue"
          />
          <StatCard
            label="Encounters"
            value={data?.metrics.totalEncounters || 0}
            icon={<ChartBarIcon className="h-4 w-4" />}
            color="emerald"
            format={formatNumber}
          />
          <StatCard
            label="Billable"
            value={data?.hierarchy.billableUsers || 0}
            icon={<CurrencyDollarIcon className="h-4 w-4" />}
            color="amber"
          />
          <StatCard
            label="Active"
            value={data?.metrics.activeOrganizations || 0}
            icon={<CheckCircleIcon className="h-4 w-4" />}
            color="green"
          />
          <StatCard
            label="Trialing"
            value={data?.metrics.trialingOrganizations || 0}
            icon={<ClockIcon className="h-4 w-4" />}
            color="cyan"
          />
          <StatCard
            label="Enterprises"
            value={data?.hierarchy.enterprises || 0}
            icon={<BuildingOfficeIcon className="h-4 w-4" />}
            color="indigo"
          />
        </div>
      </PageTransitionItem>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Platform Hierarchy Card */}
        <PageTransitionItem>
          <AnimatedCard variant="elevated" className="h-full">
            <AnimatedCardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Platform Hierarchy</h2>
                <BuildingOfficeIcon className="h-5 w-5 text-purple-500" />
              </div>
              <div className="space-y-4">
                <HierarchyItem
                  label="Enterprises"
                  value={data?.hierarchy.enterprises || 0}
                  color="indigo"
                  onClick={() => navigate('/management/enterprises')}
                />
                <HierarchyItem
                  label="Channel Partners"
                  value={data?.hierarchy.channelPartners || 0}
                  color="violet"
                  indent
                  onClick={() => navigate('/management/partners')}
                />
                <HierarchyItem
                  label="Organizations"
                  value={data?.hierarchy.organizations || 0}
                  color="purple"
                  indent
                  onClick={() => navigate('/management/organizations')}
                />
                <HierarchyItem
                  label="Billable Users"
                  value={data?.hierarchy.billableUsers || 0}
                  color="amber"
                  indent
                  onClick={() => navigate('/management/users')}
                />
              </div>
            </AnimatedCardContent>
          </AnimatedCard>
        </PageTransitionItem>

        {/* Requires Attention Card */}
        <PageTransitionItem>
          <AnimatedCard variant="elevated" className="h-full">
            <AnimatedCardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Requires Attention</h2>
                {totalAttentionItems > 0 && (
                  <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold rounded-full bg-red-100 text-red-800">
                    {totalAttentionItems}
                  </span>
                )}
              </div>
              {totalAttentionItems === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                  <CheckCircleIcon className="h-12 w-12 mb-2" />
                  <p className="text-sm">All clear! No items require attention.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {data?.attention.highUsageOrgs > 0 && (
                    <AttentionItem
                      icon={<ExclamationTriangleIcon className="h-4 w-4" />}
                      label="Orgs at 90%+ usage"
                      count={data.attention.highUsageOrgs}
                      color="amber"
                      onClick={() => navigate('/management/subscriptions')}
                    />
                  )}
                  {data?.attention.expiringTrials > 0 && (
                    <AttentionItem
                      icon={<ClockIcon className="h-4 w-4" />}
                      label="Trials expire in 7 days"
                      count={data.attention.expiringTrials}
                      color="blue"
                      onClick={() => navigate('/management/organizations')}
                    />
                  )}
                </div>
              )}
            </AnimatedCardContent>
          </AnimatedCard>
        </PageTransitionItem>

        {/* Recent Activity Card */}
        <PageTransitionItem>
          <AnimatedCard variant="elevated" className="h-full">
            <AnimatedCardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
                <ArrowTrendingUpIcon className="h-5 w-5 text-green-500" />
              </div>
              {data?.recentActivity && data.recentActivity.length > 0 ? (
                <div className="space-y-3">
                  {data.recentActivity.slice(0, 5).map((activity, idx) => (
                    <div key={idx} className="flex items-start gap-3 text-sm">
                      <div className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-purple-500" />
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-900">
                          New org <span className="font-medium">"{activity.organizationName}"</span> created
                          {activity.partnerName && (
                            <span className="text-gray-500"> by {activity.partnerName}</span>
                          )}
                        </p>
                        <p className="text-xs text-gray-400">{formatTimeAgo(activity.timestamp)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                  <ClockIcon className="h-12 w-12 mb-2" />
                  <p className="text-sm">No recent activity</p>
                </div>
              )}
            </AnimatedCardContent>
          </AnimatedCard>
        </PageTransitionItem>
      </div>

      {/* 30-Day Trends */}
      <PageTransitionItem>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <AnimatedCard variant="gradient">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">New Organizations (30d)</p>
                  <p className="text-3xl font-bold text-gray-900">{data?.trends.newOrganizationsLast30Days || 0}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <BuildingOfficeIcon className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </div>
          </AnimatedCard>
          <AnimatedCard variant="gradient">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">New Users (30d)</p>
                  <p className="text-3xl font-bold text-gray-900">{data?.trends.newUsersLast30Days || 0}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <UserGroupIcon className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>
          </AnimatedCard>
        </div>
      </PageTransitionItem>

      {/* Quick Actions */}
      <PageTransitionItem>
        <AnimatedCard variant="elevated">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <QuickActionButton
                label="Manage Organizations"
                icon={<BuildingOfficeIcon className="h-5 w-5" />}
                onClick={() => navigate('/management/organizations')}
              />
              <QuickActionButton
                label="Manage Users"
                icon={<UserGroupIcon className="h-5 w-5" />}
                onClick={() => navigate('/management/users')}
              />
              <QuickActionButton
                label="Subscriptions"
                icon={<CurrencyDollarIcon className="h-5 w-5" />}
                onClick={() => navigate('/management/subscriptions')}
              />
              {hasPermission('platform.ai_analytics.view') ? (
                <QuickActionButton
                  label="AI Cost Dashboard"
                  icon={<ChartBarIcon className="h-5 w-5" />}
                  onClick={() => navigate('/management/ai-costs')}
                />
              ) : (
                <QuickActionButton
                  label="Usage"
                  icon={<ChartPieIcon className="h-5 w-5" />}
                  onClick={() => navigate('/management/usage')}
                />
              )}
            </div>
          </div>
        </AnimatedCard>
      </PageTransitionItem>

      {/* System Status */}
      <PageTransitionItem>
        <motion.div
          className="bg-gradient-to-r from-purple-600 to-pink-600 shadow-md rounded-lg p-6 text-white"
          whileHover={{ scale: 1.005 }}
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">System Status</h3>
              <p className="mt-1 text-purple-100">All services operational</p>
            </div>
            <div className="flex items-center space-x-2">
              <motion.div
                className="h-3 w-3 bg-green-400 rounded-full"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
              <span className="text-sm font-medium">Online</span>
            </div>
          </div>
        </motion.div>
      </PageTransitionItem>
    </PageTransition>
  );
};

// Sub-components
interface StatCardProps {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  format?: (num: number) => string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color, format }) => {
  const colorClasses: Record<string, string> = {
    violet: 'bg-violet-100 text-violet-600',
    purple: 'bg-purple-100 text-purple-600',
    blue: 'bg-blue-100 text-blue-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    amber: 'bg-amber-100 text-amber-600',
    green: 'bg-green-100 text-green-600',
    cyan: 'bg-cyan-100 text-cyan-600',
    indigo: 'bg-indigo-100 text-indigo-600'
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-center gap-2 mb-1">
        <div className={`p-1.5 rounded-md ${colorClasses[color] || colorClasses.purple}`}>
          {icon}
        </div>
      </div>
      <p className="text-2xl font-bold text-gray-900">{format ? format(value) : value.toLocaleString()}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
};

interface HierarchyItemProps {
  label: string;
  value: number;
  color: string;
  indent?: boolean;
  onClick?: () => void;
}

const HierarchyItem: React.FC<HierarchyItemProps> = ({ label, value, color, indent, onClick }) => {
  const colorClasses: Record<string, string> = {
    indigo: 'bg-indigo-500',
    violet: 'bg-violet-500',
    purple: 'bg-purple-500',
    amber: 'bg-amber-500'
  };

  return (
    <motion.div
      className={`flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 cursor-pointer ${indent ? 'ml-4' : ''}`}
      onClick={onClick}
      whileHover={{ x: 4 }}
    >
      <div className="flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${colorClasses[color] || colorClasses.purple}`} />
        <span className="text-sm text-gray-700">{label}</span>
      </div>
      <span className="text-sm font-semibold text-gray-900">{value.toLocaleString()}</span>
    </motion.div>
  );
};

interface AttentionItemProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  color: string;
  onClick?: () => void;
}

const AttentionItem: React.FC<AttentionItemProps> = ({ icon, label, count, color, onClick }) => {
  const colorClasses: Record<string, { bg: string; text: string; badge: string }> = {
    red: { bg: 'bg-red-50', text: 'text-red-700', badge: 'bg-red-100 text-red-800' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-800' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-800' }
  };

  const classes = colorClasses[color] || colorClasses.red;

  return (
    <motion.div
      className={`flex items-center justify-between p-3 rounded-lg ${classes.bg} cursor-pointer`}
      onClick={onClick}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="flex items-center gap-2">
        <span className={classes.text}>{icon}</span>
        <span className={`text-sm font-medium ${classes.text}`}>{label}</span>
      </div>
      <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${classes.badge}`}>
        {count}
      </span>
    </motion.div>
  );
};

interface QuickActionButtonProps {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}

const QuickActionButton: React.FC<QuickActionButtonProps> = ({ label, icon, onClick }) => (
  <motion.button
    onClick={onClick}
    className="flex items-center justify-center gap-2 p-4 rounded-lg border-2 border-purple-200 text-purple-700 hover:bg-purple-50 transition-colors"
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
  >
    {icon}
    <span className="text-sm font-medium">{label}</span>
  </motion.button>
);

export default ManagementDashboard;
